import { useEffect, useMemo, useState } from "react";
import {
  collection,
  getDocs,
  doc,
  updateDoc,
  orderBy,
  query,
  Timestamp,
} from "firebase/firestore";
import { db } from "@/firebase/firebaseConfig";
import { useTheme } from "@/utils/theme-context";
import { Select as AntSelect } from "antd";
const { Option } = AntSelect;
import {
  Table,
  Input,
  Checkbox,
  Button as AntButton,
  message,
  Card,
} from "antd";

export default function Users() {
  const [users, setUsers] = useState([]);
  const [editedUsers, setEditedUsers] = useState({});
  const [searchTerm, setSearchTerm] = useState("");

  const [messageApi, contextHolder] = message.useMessage();

  const filteredUsers = useMemo(() => {
    return users.filter((user) =>
      user.email.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [searchTerm, users]);

  const accessOptions = [
    { value: "course", label: "Курсы" },
    { value: "productList", label: "Список товаров" },
    { value: "bonusLessons", label: "Бонусные уроки" },
    { value: "chats", label: "Чат" },
    { value: "courseDropshipping", label: "Курсы (Дропшиппинг)" },
    {
      value: "bonusLessonsDropshipping",
      label: "Бонусные уроки (Дропшиппинг)",
    },
    {
      value: "ai",
      label: "ИИ",
    },
    {
      value: "invest",
      label: "Инвестор",
    },
    {
      value: "telegram",
      label: "Телеграм",
    },
    {
      value: "orders",
      label: "Заказы",
    },
  ];

  useEffect(() => {
    const usersCollectionRef = collection(db, "users");
    const getUsers = async () => {
      try {
        const q = query(usersCollectionRef, orderBy("createdAt", "desc")); // Сортировка по убыванию
        const querySnapshot = await getDocs(q);

        const usersList = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        setUsers(usersList);
      } catch (error) {
        console.error("Ошибка при получении пользователей:", error);
      }
    };

    getUsers();
  }, []);

  const handleSave = async (user, e) => {
    e.preventDefault();
    const userDocRef = doc(db, "users", user.id);

    try {
      const edited = editedUsers[user.id] ?? {};
      const newAccess = edited.access ?? user.access;
      const hadChats = user.access.includes("chats");
      const hasChatsNow = newAccess.includes("chats");
      const isManual = !hadChats && hasChatsNow;

      const updatedData = {
        maxModules: parseInt(edited.maxModules ?? user.maxModules),
        isVerified: edited.isVerified ?? user.isVerified,
        access: newAccess,
        ...(isManual && { manualAccessExtension: true }),
      };

      await updateDoc(userDocRef, updatedData);
      messageApi.info("Пользователь обновлен");

      setUsers((prevUsers) =>
        prevUsers.map((u) => (u.id === user.id ? { ...u, ...updatedData } : u))
      );

      setEditedUsers((prev) => {
        const newState = { ...prev };
        delete newState[user.id];
        return newState;
      });
    } catch (error) {
      console.error("Ошибка при обновлении документа:", error);
    }
  };

  const handleEditChange = (id, field) => (e) => {
    const value = field === "isVerified" ? e.target.checked : e.target.value;

    setEditedUsers((prev) => ({
      ...prev,
      [id]: { ...prev[id], [field]: value },
    }));
  };

  const handleAccessChange = (userId) => (selectedValues) => {
    setEditedUsers((prev) => ({
      ...prev,
      [userId]: {
        ...prev[userId],
        access: selectedValues,
      },
    }));
  };

  const { theme } = useTheme();

  const formattedDate = (date) => {
    const data = date.toDate();
    const formatted = data.toLocaleString("ru-RU", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
    return formatted;
  };

  const userStats = useMemo(() => {
    const now = new Date();
    const startOfToday = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate()
    );
    const oneWeekAgo = new Date(now);
    oneWeekAgo.setDate(now.getDate() - 7);
    const oneMonthAgo = new Date(now);
    oneMonthAgo.setMonth(now.getMonth() - 1);

    const getDate = (createdAt) => {
      if (createdAt instanceof Timestamp) return createdAt.toDate();
      return new Date(createdAt);
    };

    let today = 0;
    let week = 0;
    let month = 0;

    for (const user of users) {
      const created = getDate(user.createdAt);
      if (created >= startOfToday) today++;
      if (created >= oneWeekAgo) week++;
      if (created >= oneMonthAgo) month++;
    }

    return {
      today,
      week,
      month,
      total: users.length,
    };
  }, [users]);

  return (
    <div className={`layout layout--${theme} users users--${theme}`}>
      {contextHolder}

      <Input
        placeholder="Поиск по Email..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        style={{ marginBottom: 16 }}
      />
      <Card title="Статистика пользователей" style={{ marginBottom: 16 }}>
        <Card.Grid style={{ width: "25%", padding: "15px" }}>
          Сегодня: {userStats?.today}
        </Card.Grid>
        <Card.Grid style={{ width: "25%", padding: "15px" }}>
          За неделю: {userStats?.week}
        </Card.Grid>
        <Card.Grid style={{ width: "25%", padding: "15px" }}>
          За месяц: {userStats?.month}
        </Card.Grid>
        <Card.Grid style={{ width: "25%", padding: "15px" }}>
          Всего: {userStats?.total}
        </Card.Grid>
      </Card>

      <Table
        rowKey="id"
        dataSource={filteredUsers}
        pagination={{ pageSize: 50 }}
        columns={[
          {
            title: "No",
            render: (_, __, index) => index + 1,
          },
          {
            title: "ID",
            dataIndex: "uid",
          },
          {
            title: "Email",
            dataIndex: "email",
          },
          {
            title: "Доступные модули",
            render: (text, user) => (
              <Input
                type="number"
                style={{ width: 80 }}
                min={0}
                value={editedUsers[user.id]?.maxModules ?? user.maxModules ?? 4}
                onChange={handleEditChange(user.id, "maxModules")}
              />
            ),
          },
          {
            title: "Проверен",
            render: (text, user) => (
              <Checkbox
                checked={editedUsers[user.id]?.isVerified ?? user.isVerified}
                onChange={handleEditChange(user.id, "isVerified")}
              />
            ),
          },
          {
            title: "Доступы",
            render: (text, user) => (
              <AntSelect
                mode="multiple"
                style={{ width: 300 }}
                value={editedUsers[user.id]?.access ?? user.access ?? []}
                onChange={(selectedValues) =>
                  handleAccessChange(user.id)(selectedValues)
                }
              >
                {accessOptions.map((option) => (
                  <Option key={option.value} value={option.value}>
                    {option.label}
                  </Option>
                ))}
              </AntSelect>
            ),
          },
          {
            title: "Дата",
            render: (text, user) => formattedDate(user.createdAt),
          },
          {
            title: "Сохранить",
            render: (text, user) => (
              <AntButton type="primary" onClick={(e) => handleSave(user, e)}>
                Сохранить
              </AntButton>
            ),
          },
        ]}
      />
    </div>
  );
}
